import React from 'react'
import { useTheme, Typography, Button, Box, Chip, Alert } from '@mui/material'
import { Check, Schedule, Warning, Info, Assignment } from '@mui/icons-material'
import { FlexDiv } from '../FlexDiv'
import { TenantMoveInInspection } from '../../types/inspection'

// Helper function to get plural endings
const getPluralEnding = (count: number) => count === 1 ? '' : 's'

// Helper function to format time remaining
const getTimeRemaining = (dueDate: string) => {
    const due = new Date(dueDate)
    const now = new Date()
    const diff = due.getTime() - now.getTime()

    if (diff <= 0) {
        return 'Overdue'
    }

    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
        const leftoverHours = hours - days * 24
        const dayEnd = getPluralEnding(days)
        const hourEnd = getPluralEnding(leftoverHours)
        return `${days} day${dayEnd}, ${leftoverHours} hour${hourEnd} remaining`
    } else if (hours > 0) {
        const leftoverMinutes = minutes - hours * 60
        const hourEnd = getPluralEnding(hours)
        const minuteEnd = getPluralEnding(leftoverMinutes)
        return `${hours} hour${hourEnd}, ${leftoverMinutes} minute${minuteEnd} remaining`
    } else {
        return `${minutes} minutes remaining`
    }
}

interface MoveInInspectionCardProps {
    inspection: TenantMoveInInspection
    leaseId: number
    onInspectionClick: (leaseId: number) => void
}

export const MoveInInspectionCard: React.FC<MoveInInspectionCardProps> = ({
    inspection,
    leaseId,
    onInspectionClick,
}) => {
    const theme = useTheme()

    const isReady = inspection.status === 'READY'
    const isComplete = inspection.status === 'COMPLETE'

    const getStatusColor = () => {
        if (isComplete) return theme.palette.success.main
        if (isReady) return theme.palette.warning.main
        return theme.palette.info.main
    }

    const getStatusIcon = () => {
        if (isComplete) return <Check />
        if (isReady) return <Schedule />
        return <Info />
    }

    const getStatusText = () => {
        if (isComplete) return 'Complete'
        if (isReady) return 'Ready'
        return 'Pending'
    }

    const getAlertSeverity = () => {
        if (isComplete) return 'success'
        if (isReady) return 'warning'
        return 'info'
    }

    const getMessageText = () => {
        if (isComplete) return 'Property inspection completed successfully'
        if (isReady) {
            return inspection.due_date
                ? getTimeRemaining(inspection.due_date)
                : 'Inspection ready to complete'
        }
        return 'Inspection will be available soon'
    }

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                borderRadius: 3,
                padding: 3,
                marginBottom: 3,
                border: `1px solid ${theme.palette.grey[700]}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Background accent */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${getStatusColor()} 0%, ${theme.palette.primary.main} 100%)`,
                }}
            />

            {/* Header */}
            <FlexDiv
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                }}
            >
                <FlexDiv style={{ alignItems: 'center' }}>
                    <Assignment
                        style={{
                            color: getStatusColor(),
                            marginRight: theme.spacing(1),
                            fontSize: 24
                        }}
                    />
                    <Typography
                        variant="h6"
                        style={{
                            color: '#fff',
                            fontWeight: 600
                        }}
                    >
                        Move-In Inspection
                    </Typography>
                </FlexDiv>
                <Chip
                    icon={getStatusIcon()}
                    label={getStatusText()}
                    color={getAlertSeverity() as any}
                    size="small"
                    style={{
                        fontWeight: 600,
                        backgroundColor: getStatusColor(),
                        color: 'white'
                    }}
                />
            </FlexDiv>



            {/* Status message */}
            <Alert
                severity={getAlertSeverity() as any}
                style={{
                    marginBottom: theme.spacing(2),
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: `1px solid ${getStatusColor()}40`,
                    borderRadius: 2,
                }}
                icon={getStatusIcon()}
            >
                <Typography
                    variant="body2"
                    style={{
                        color: '#fff',
                        fontWeight: 500
                    }}
                >
                    {getMessageText()}
                </Typography>
            </Alert>

            {/* Action button */}
            {(isReady || isComplete) && (
                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => onInspectionClick(leaseId)}
                    startIcon={<Assignment />}
                    sx={{
                        backgroundColor: getStatusColor(),
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        padding: theme.spacing(1.5, 2),
                        borderRadius: 2,
                        boxShadow: `0 4px 15px ${getStatusColor()}40`,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                            boxShadow: `0 6px 20px ${getStatusColor()}60`,
                        }
                    }}
                >
                    {isComplete ? 'View Inspection' : 'Complete Inspection Now'}
                </Button>
            )}

            {/* Due date */}
            {inspection.due_date && (
                <Box
                    sx={{
                        marginTop: theme.spacing(2),
                        padding: theme.spacing(1.5),
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.grey[600]}`,
                    }}
                >
                    <FlexDiv style={{ alignItems: 'center' }}>
                        <Schedule
                            style={{
                                color: '#8b949e',
                                marginRight: theme.spacing(1),
                                fontSize: 16
                            }}
                        />
                        <Typography
                            variant="caption"
                            style={{
                                color: '#8b949e',
                                fontWeight: 500
                            }}
                        >
                            Due: {new Date(inspection.due_date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </Typography>
                    </FlexDiv>
                </Box>
            )}
        </Box>
    )
}